import React, { useCallback, useState } from 'react'
import { Platform } from 'react-native'
import { RegionType, TokenType } from '@vatom/sdk/core'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { VatomTokenType } from '@vatom/vatom/plugin'
import { AVPlaybackStatus } from 'expo-av'

import logger from '../../../logger'
import { useCrateStore, useSDK } from '../../../store'
import { FaceProps } from '../../FaceProvider'
import ImageFace from '../ImageFace'
import LoaderView from '../LoaderView'

import { RenderCover } from './Cover'
import { useVideoComponent } from './useVideoComponent'

export * from './Cover'

export const isVatom = (token: TokenType): token is VatomTokenType => token.type === 'vatom'

function VideoFace(props: FaceProps) {
  const { token, viewMode: VM, onMessage, ...rest } = props
  const viewMode = VM || 'icon'
  const vatom = token as VatomTokenType
  const face = vatom.getFace(viewMode)
  const [shouldVideoStart, setShouldVideoStart] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const isCrate = isVatom(token) && token.isCrate
  const unlockKey = isVatom(token) && token.unlockKey(viewMode)
  const coverImage = token?.resources.find(r => r.name === 'CoverImage')
  const animation_url = token?.metadata.animation_url

  const crateVersion = token.getCrateVersion
  const face_config = JSON.parse(face?.properties?.config || '{}') as CrateConfigFace
  const { Video, ref, pauseVideo, playVideo } = useVideoComponent()
  const sdk = useSDK()

  const handleCrateWithKey = () => {
    const region = sdk.dataPool.region(RegionType.inventory)

    const token = region.tokens.find(
      tk => isVatom(tk) && tk?.properties?.template_variation === unlockKey
    )

    if (token) {
      token.performAction('Delete').catch(error => {
        console.error('Error deleting crate', error)
      })
      setShouldVideoStart(true)
      onPlayVideo()
      return
    }
    console.log('User clicked to activate the video, but we have an unlock key. Ignored.')
  }

  const onPlayVideo = () => {
    callCrateV3()
    playVideo()
  }

  const callCrateV3 = () => {
    if (isCrate && crateVersion === 'crate-v3') {
      useCrateStore.getState().setisRewardsActive()
      callAction()
    }
  }

  const onCoverClick = (shouldStart: boolean) => {
    if (isCrate) {
      handleCrateWithKey()
      return
    }

    setShouldVideoStart(true)
    onPlayVideo()
  }

  const callAction = useCallback(async () => {
    if (isCrate && isVatom(token)) {
      const face_action_name = face_config.activate_action
      const vatom_action_name = vatom.properties.activate_action
      const actionName = face_action_name || vatom_action_name || 'Activate'
      try {
        const response = await vatom.performAction(actionName, null)

        const res = response?.pre as CrateResponse

        if (res) {
          const vatoms: PreCreatedVatoms = []
          res.map(_ => {
            _.output.output.vatoms.map(v => vatoms.push(v))
          })

          useCrateStore
            .getState()
            .storeCrateNotification({ tokens: vatoms, token: vatom, config: face_config })
        }
        // if (!useCrateStore.getState().didCrateClose) {
        //   useCrateStore.getState().openRewardCard()
        // }
        logger.info(res)
      } catch (error) {
        logger.error(error)
      }
    }
  }, [face_config, isCrate, token, vatom])

  // Handler video´s states
  const onStatusChange = (videoStatus: AVPlaybackStatus) => {
    if (!videoStatus.isLoaded) {
      return
    }
    if (!videoStatus.durationMillis) {
      return
    }

    const remainingDurationMillis = videoStatus.durationMillis - videoStatus.positionMillis

    if (face_config.toastAppearanceTime) {
      const targetAppearance =
        face_config.toastAppearanceTime >= 0
          ? face_config.toastAppearanceTime * 1000
          : videoStatus.durationMillis - Math.abs(face_config.toastAppearanceTime * 1000)
      if (
        targetAppearance <= videoStatus.positionMillis &&
        !useCrateStore.getState().shouldShowCrateRewards
      ) {
        useCrateStore.getState().openRewardCard()
        // }
      }
    } else {
      if (remainingDurationMillis <= 1000 && !useCrateStore.getState().shouldShowCrateRewards) {
        useCrateStore.getState().openRewardCard()
      }
    }

    if (
      isCrate &&
      videoStatus.didJustFinish &&
      !useCrateStore.getState().didVideoReproductionEnded
    ) {
      pauseVideo()

      if (crateVersion === 'crate-v2') {
        callAction()
      }

      if (onMessage) {
        onMessage('viewer.view.close', {})

        // const state = navigation.getState()
        // const key = state.routes[state.index - 1].key
        // navigation.dispatch({
        //   ...CommonActions.goBack(),
        //   source: key,
        //   target: key
        // })

        // navigation.dispatch(StackActions.pop(2))
        // navigation.dispatch(CommonActions.navigate(AppRoutes.RewardsCard))

        token.performAction('Delete').catch(error => {
          console.error('Error deleting crate', error)
        })
        useCrateStore.getState().setDidVideoReproductionEnded(true)
      }
    }

    if (!isCrate && videoStatus.positionMillis === videoStatus.durationMillis) {
      ref.current?.setPositionAsync(0)
    }

    return
  }

  if (viewMode === 'icon') {
    return <ImageFace token={token} viewMode={viewMode} {...rest} />
  }

  const getLooping = () => {
    if (isCrate) {
      return false
    }

    return face_config.loop || token.studioInfo?.businessId === 'nQwtevgfOa' //hack 4 pepsi campaign
  }

  const onLoadVideo = () => {
    setIsLoading(false)
    if (isCrate) {
      if (unlockKey) {
        handleCrateWithKey()
        return
      }

      if (coverImage) {
        return
      }

      onPlayVideo()
    }

    if (coverImage) {
      return
    }

    onPlayVideo()
  }

  if (!animation_url) return null

  return (
    <>
      {Platform.OS !== 'web' && isLoading ? <LoaderView /> : null}

      {!shouldVideoStart && coverImage?.url ? (
        <RenderCover url={coverImage?.url} onCoverClick={onCoverClick} />
      ) : null}

      <Video
        ref={ref}
        source={{ uri: animation_url }}
        onLoad={onLoadVideo}
        onPlaybackStatusUpdate={onStatusChange}
        useNativeControls={!isCrate}
        isLooping={getLooping()}
      />
    </>
  )
}

Object.defineProperty(VideoFace, 'name', {
  writable: true,
  value: 'VideoFace'
})

export default VideoFace

export type CrateConfigFace = {
  showConfetti?: boolean
  toastAppearanceTime: number
  video: {
    ref: string
    type: string
  }
  activate_action?: string
  loop?: boolean
}

type VatomOutput<T> = {
  error: null
  output: T
}

export type PreCreatedVatom = {
  icon: string
  name: string
}

export type PreCreatedVatoms = PreCreatedVatom[]

type CrateResponse = VatomOutput<
  VatomOutput<{
    vatoms: PreCreatedVatom[]
  }>
>[]

// const fake_face_config = {
//   showConfetti: true,
//   toastAppearanceTime: 2,
//   video: {
//     ref: 'https://resources.vatominc.com/rUBK9GTeKK/a661f7c0-ba12-11ee-a841-8576171757eb.mp4?1706029975867',
//     type: 'video/mp4'
//   }
// }

// const fake_response = {
//   main: {
//     error: null
//   },
//   pre: [
//     {
//       error: null,
//       output: {
//         error: null,
//         output: {
//           vatoms: [
//             {
//               icon: 'https://resources.varius.io/rUBK9GTeKK/9a3cbed9-3765-4613-bc78-3069460373b7.png',
//               id: '2YGYPpghm6',
//               name: 'My 3D Model NFT'
//             }
//           ]
//         }
//       }
//     }
//   ],
//   post: null
// }
